import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadCurrent } from 'app/state/utils/generation'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { ToDoState } from './reducer'
import { Dispatch } from 'redux'
import {
  getTodos,
  getTodo,
  addTodo,
  editTodo,
  deleteTodos,
  startLoadingTodos,
  stopLoadingTodos,
  todosLoadFailed,
} from './actions'
import { ToDoParams } from 'app/models/todo-params'

export const saveTodo =
  (savedTodo: ToDoParams, todo?: ToDoParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    if (todo) {
      savedTodo.id = todo.id
      return client
        .put(`${apiServerUrl}/api/todo-lists`, savedTodo)
        .then(todo => {
          dispatch(editTodo(todo))
          return { success: true, payload: todo.data }
        })
        .catch(error => {
          return { success: false, payload: error.response.data.error }
        })
    }
    delete savedTodo.id
    return client
      .post(`${apiServerUrl}/api/todo-lists`, savedTodo)
      .then(todo => {
        dispatch(addTodo(todo))
        return { success: true, payload: todo.data }
      })
      .catch(error => {
        return { success: false, payload: error.response.data.error }
      })
  }

type GetState = () => { todos: ToDoState }

/** One list read at a time: a second caller joins the one in flight. */
let inFlight: Promise<unknown> | null = null

/**
 * The To Do tab's one read (ADR_LOS-030). The response carries the store's
 * generation at the time of the request; a write that lands meanwhile makes
 * the reducer drop it and this reads once more (utils/generation.ts). On
 * failure the store keeps what it had and the user is told.
 */
export const loadTodos = () => (dispatch: Dispatch, getState?: GetState) => {
  if (inFlight) return inFlight
  const generation = () => getState?.().todos.generation ?? 0
  dispatch(startLoadingTodos())
  inFlight = loadCurrent(generation, g => load(g, dispatch)).finally(() => {
    inFlight = null
    dispatch(stopLoadingTodos())
  })
  return inFlight
}

const load = async (generation: number, dispatch: Dispatch) => {
  try {
    const response = await client.get(`${config.backendUrl}/api/todo-lists`)
    const allTodos = (response.data ?? []).map((todo: ToDoParams) => ({ ...todo }))
    return dispatch(getTodos(allTodos, generation))
  } catch (e: any) {
    dispatch(todosLoadFailed())
    ErrorNotification({
      title: 'Could not load the to do list',
      message:
        e?.response?.data?.error ??
        e?.response?.data?.message ??
        'Reload the page to try again.',
    })
  }
}

export const loadOneTodo = (todoId: string) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/todo-lists/one?id=${todoId}`)
    .then(todo => {
      return dispatch(getTodo({ ...todo.data }))
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const changeTodoStatus =
  (todo: ToDoParams, status: string) => async (dispatch: Dispatch) => {
    todo.status = status
    const apiServerUrl = config.backendUrl
    return client
      .put(`${apiServerUrl}/api/todo-lists`, todo)
      .then(todo => {
        dispatch(editTodo(todo))
        return { success: true, payload: todo.data }
      })
      .catch(error => {
        return { success: false, payload: error.response.data.error }
      })
  }

export const removeTodos = (todo: string) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .delete(`${apiServerUrl}/api/todo-lists?ids[]=${todo}`)
    .then(todos => {
      dispatch(deleteTodos(todos.data))
      return { success: true, payload: todos.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}
